import { useState, useMemo } from 'react';
import { MessageSquare, Terminal, Search } from 'lucide-react';
import { useSessionsStore } from '../../stores/sessionsStore';
import { Spinner } from '../common/Spinner';
import { EmptyState } from '../common/EmptyState';
import type { TranscriptEntry } from '../../types/docs';

type DateRange = 'all' | 'today' | '7d' | '30d';
type SourceFilter = 'all' | 'secondbrain' | 'claude-code';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function formatTime12h(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${suffix}`;
}

function groupByDate(entries: TranscriptEntry[]): Map<string, TranscriptEntry[]> {
  const groups = new Map<string, TranscriptEntry[]>();
  for (const entry of entries) {
    if (!groups.has(entry.date)) groups.set(entry.date, []);
    groups.get(entry.date)!.push(entry);
  }
  return groups;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function getDateCutoff(range: DateRange): string | null {
  if (range === 'all') return null;
  const now = new Date();
  if (range === 'today') {
    now.setDate(now.getDate() - 1);
    return now.toLocaleDateString('en-CA');
  }
  const days = range === '7d' ? 7 : 30;
  now.setDate(now.getDate() - days);
  return now.toLocaleDateString('en-CA');
}

export function SessionList() {
  const { transcripts, transcriptsLoading, selectedTranscript, selectTranscript } = useSessionsStore();
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');

  const filtered = useMemo(() => {
    let result = transcripts;

    if (sourceFilter !== 'all') {
      result = result.filter(t => t.source === sourceFilter);
    }

    const cutoff = getDateCutoff(dateRange);
    if (cutoff) {
      result = result.filter(t => t.date >= cutoff);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      result = result.filter(t =>
        t.filename.toLowerCase().includes(q) ||
        t.date.includes(q) ||
        formatDate(t.date).toLowerCase().includes(q) ||
        (t.label && t.label.toLowerCase().includes(q))
      );
    }

    return result;
  }, [transcripts, search, dateRange, sourceFilter]);

  if (transcriptsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="sm" />
      </div>
    );
  }

  if (transcripts.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          icon={<MessageSquare />}
          title="No transcripts"
          description="No session transcripts found on VPS."
        />
      </div>
    );
  }

  const grouped = groupByDate(filtered);
  const selectedFilename = selectedTranscript?.filename;

  const sbCount = transcripts.filter(t => t.source === 'secondbrain').length;
  const ccCount = transcripts.filter(t => t.source === 'claude-code').length;

  return (
    <div className="flex flex-col h-full">
      {/* Filter bar */}
      <div className="flex-shrink-0 px-2 pt-2 pb-1 space-y-1.5 border-b border-border-subtle">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-text-muted" />
          <input
            type="text"
            placeholder="Search sessions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-7 pr-2 py-1 text-xs bg-bg-tertiary border border-border-subtle rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple/50"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value as SourceFilter)}
            className="text-[10px] bg-bg-tertiary border border-border-subtle rounded px-1.5 py-0.5 text-text-secondary focus:outline-none focus:border-accent-purple/50"
          >
            <option value="all">All ({transcripts.length})</option>
            <option value="secondbrain">SecondBrain ({sbCount})</option>
            <option value="claude-code">Claude Code ({ccCount})</option>
          </select>
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value as DateRange)}
            className="text-[10px] bg-bg-tertiary border border-border-subtle rounded px-1.5 py-0.5 text-text-secondary focus:outline-none focus:border-accent-purple/50"
          >
            <option value="all">All time</option>
            <option value="today">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
          <span className="text-[10px] text-text-muted ml-auto">
            {filtered.length}
          </span>
        </div>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-text-muted text-center">No matching sessions</div>
        ) : (
          Array.from(grouped.entries()).map(([date, entries]) => (
            <div key={date} className="mb-2">
              <div className="px-3 py-1 text-[10px] font-medium text-text-muted uppercase tracking-wide">
                {formatDate(date)}
              </div>
              {entries.map(entry => (
                <button
                  key={entry.filename}
                  onClick={() => selectTranscript(entry.filename)}
                  className={`w-full flex items-start gap-2 px-3 py-1.5 text-left rounded transition-colors ${
                    selectedFilename === entry.filename
                      ? 'bg-accent-purple/20 text-accent-purple-light'
                      : 'hover:bg-bg-tertiary'
                  }`}
                >
                  {entry.source === 'claude-code'
                    ? <Terminal className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-emerald-400" />
                    : <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-text-muted" />
                  }
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-text-primary truncate">
                      {formatTime12h(entry.time)}
                      <span className="ml-1.5 text-[9px] px-1 py-px rounded bg-bg-tertiary text-text-muted">
                        {entry.source === 'claude-code' ? 'CC' : 'SB'}
                      </span>
                    </div>
                    <div className="text-[10px] text-text-muted">
                      {formatSize(entry.size)}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
